import { useNavigate, useParams } from "react-router-dom";
import { useLanguageContext } from "@/contexts/LanguageContext";
import type { Locale } from "@/i18n";

/**
 * useLocalizedNavigate — wrapper autour de useNavigate qui préfixe
 * automatiquement les chemins avec la locale courante (/fr/ ou /ar/).
 *
 * Usage: const nav = useLocalizedNavigate();
 *        nav("/annonces"); // navigue vers /fr/annonces (ou /ar/annonces)
 */
export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const { locale } = useLanguageContext();

  return (to: string, options?: Parameters<ReturnType<typeof useNavigate>>[1]) => {
    // Éviter le double préfixe si la route commence déjà par /fr/ ou /ar/
    if (to.startsWith(`/${locale}/`) || to === `/${locale}`) {
      navigate(to, options);
      return;
    }
    const normalized = to.startsWith("/") ? to : `/${to}`;
    navigate(`/${locale}${normalized}`, options);
  };
}

/**
 * useLocalizedPath — retourne un chemin préfixé avec la locale donnée
 * (ou la locale courante si non spécifiée).
 */
export function useLocalizedPath() {
  const { locale } = useLanguageContext();

  return (path: string, overrideLocale?: Locale) => {
    const lang = overrideLocale ?? locale;
    const normalized = path.startsWith("/") ? path : `/${path}`;
    return `/${lang}${normalized}`;
  };
}
